import React, { useState } from 'react';
import clsx from 'clsx';
import { Typography } from '../../../_shared/components/Typography/Typography';
import { KpiFactsTableData } from '../../../_shared/types';
import { Card } from '../../../_shared/components/Card/Card';
import { Input } from '../../../_shared/components/Input/Input';
import { ReactComponent as InfoIcon } from '../../../_shared/assets/icons/info-icon.svg';
import { Button } from '../../../_shared/components/Button/Button';
import styles from './CardDataMobile.module.scss';

interface Props {
  content: KpiFactsTableData[];
  sentKpis: Set<string>;
  onSend: (kpiId: string) => void;
  onDiscuss: () => void;
  onMethodologyClick?: (kpiId: string) => void;
}

function formatValue(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('ru-RU');
}

const PERIOD_LABELS: Record<string, string> = {
  '6months': 'Факт, 6 мес',
  '9months': 'Факт, 9 мес',
  '12months': 'Факт, год'
};

export function CardDataMobile({
  content: initialContent,
  sentKpis,
  onSend,
  onDiscuss,
  onMethodologyClick
}: Props) {
  const [data, setData] = useState(initialContent);

  const handleChange = (
    kpiId: string,
    responsibleId: string,
    period: string,
    rawValue: string
  ) => {
    const numValue = rawValue.trim() === '' ? null : Number(rawValue);
    setData((prev) =>
      prev.map((kpi) => {
        if (kpi.kpiId !== kpiId) return kpi;
        return {
          ...kpi,
          responsibles: kpi.responsibles.map((person) => {
            if (person.id !== responsibleId) return person;
            return {
              ...person,
              facts: person.facts.map((fact) => {
                if (fact.period !== period) return fact;
                return { ...fact, value: numValue };
              })
            };
          })
        };
      })
    );
  };

  return (
    <div className={styles.list}>
      {data.map((kpi) => {
        const isSent = sentKpis.has(kpi.kpiId);

        return (
          <div key={kpi.kpiId} className={styles.card}>
            <div className={styles.head}>
              <div className={styles.cardHeader}>
                <Typography variant={'h4'}>
                  {kpi.kpiId}. {kpi.kpiName}, {kpi.unit}
                </Typography>
              </div>
              <div
                className={styles.methodLink}
                onClick={() => onMethodologyClick?.(kpi.kpiId)}
              >
                Методика расчета &nbsp; <InfoIcon className={styles.infoIcon} />
              </div>
            </div>

            <Card className={styles.cardBlock}>
              {kpi.responsibles.map((row, index) => (
                <div key={row.id} className={styles.responsibleCard}>
                  <div className={styles.responsibleHeader}>
                    <div className={styles.responsibleHeaderLeft}>
                      <Typography variant={'h4'}>
                        {index + 1}. {row.fullName}
                      </Typography>
                      <Typography variant={'text'} className={styles.position}>
                        {row.position}
                      </Typography>
                    </div>
                    {isSent && row.hasAppeal && (
                      <button
                        type="button"
                        className={clsx(
                          styles.discussBtn,
                          styles.discussBtnTablet
                        )}
                        onClick={onDiscuss}
                      >
                        Обсудить
                      </button>
                    )}
                  </div>

                  <div className={styles.valuesGrid}>
                    <div className={styles.valueYear}>
                      <Typography variant={'text'} className={styles.textGray}>
                        {kpi.year}
                      </Typography>
                    </div>
                    <div className={styles.valueRowPlan}>
                      <div className={styles.valueLabel}>
                        <Typography
                          variant={'text'}
                          className={styles.textPlan}
                        >
                          План
                        </Typography>
                      </div>
                      <div className={styles.value}>
                        <Typography
                          variant={'text'}
                          className={styles.textGray}
                        >
                          {formatValue(row.plan)}
                        </Typography>
                      </div>
                    </div>

                    {row.facts.map((fact, fIndex) => {
                      const isYearFact = fact.period === '12months';
                      const periodLabel =
                        PERIOD_LABELS[fact.period] ?? fact.period;
                      const showInput = fact.isEditable && !isSent;

                      return (
                        <div
                          key={fIndex}
                          className={clsx(styles.valueRow, {
                            [styles.factYearRow]: isYearFact,
                            [styles.editing]: showInput
                          })}
                        >
                          <div className={styles.valueLabel}>
                            <Typography
                              variant={'text'}
                              className={styles.textGray}
                            >
                              {periodLabel}
                            </Typography>
                          </div>
                          <div className={styles.value}>
                            {showInput ? (
                              <Input
                                label=""
                                value={fact.value?.toString() ?? ''}
                                onChange={(e) =>
                                  handleChange(
                                    kpi.kpiId,
                                    row.id,
                                    fact.period,
                                    e.target.value
                                  )
                                }
                                type="number"
                                className={styles.editInputWrapper}
                                variant={'thin'}
                              />
                            ) : (
                              <Typography
                                variant={'text'}
                                className={styles.textGray}
                              >
                                {formatValue(fact.value)}
                              </Typography>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {isSent && (
                      <div className={styles.valueRow}>
                        <div className={styles.valueLabel}>
                          <Typography
                            variant={'text'}
                            className={styles.textGray}
                          >
                            Апелляция
                          </Typography>
                        </div>
                        <div className={styles.value}>
                          <Typography
                            variant={'text'}
                            className={styles.textGray}
                          >
                            {row.hasAppeal ? 'Есть' : '—'}
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>

                  {isSent && row.hasAppeal && (
                    <button
                      type="button"
                      className={clsx(
                        styles.discussBtn,
                        styles.discussBtnMobile
                      )}
                      onClick={onDiscuss}
                    >
                      Обсудить
                    </button>
                  )}
                </div>
              ))}

              <Button
                variant={isSent ? 'disabled' : 'primary'}
                disabled={isSent}
                className={styles.button}
                onClick={() => !isSent && onSend(kpi.kpiId)}
              >
                {isSent
                  ? 'Отправлено на ознакомление'
                  : 'Отправить на ознакомление'}
              </Button>
            </Card>
          </div>
        );
      })}
    </div>
  );
}
